import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Volume2 } from 'lucide-react';
import peke1Dream from '@/assets/animations/peke/v1/peke-1-dream.jpg';
import peke2Thinking from '@/assets/animations/peke/v1/peke-2-thinking.jpg';
import peke3Celebrating from '@/assets/animations/peke/v1/peke-3-celebrating.jpg';
import { animalAlphabetData } from '@/data/animalAlphabetGrid';
import {
  initAudioUnlock,
  playClickFeedback,
  prefetch,
  prefetchCombinedUtterances,
  speak,
  speakLetterAnimalAndNature,
  stop as stopVoice,
} from '@/lib/naturalVoiceService';

interface SimplifiedGameProps {
  language: 'en' | 'es';
  onComplete?: () => void;
}

// Animal ABCs - tap a letter to hear it spoken with the animal + nature note.
// Children trace on paper while the teacher narrates.
export function AnimalABCsSimplified({ language }: SimplifiedGameProps) {
  const [selectedLetter, setSelectedLetter] = useState('A');
  const [selectedNumber, setSelectedNumber] = useState(1);

  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const t = (en: string, es: string) => (language === 'en' ? en : es);

  // Per-language flat lookup so tap → speak is a constant-time read.
  const byLetter = useMemo(() => {
    return animalAlphabetData.reduce<
      Record<
        string,
        { animal: string; emoji: string; natureFact: string }
      >
    >((acc, row) => {
      acc[row.letter] = {
        animal: language === 'en' ? row.animalEn : row.animalEs,
        emoji: language === 'en' ? row.emojiEn : row.emojiEs,
        natureFact: language === 'en' ? row.funFactEn : row.funFactEs,
      };
      return acc;
    }, {});
  }, [language]);

  // Warm the TTS cache once per language so taps don't wait on a network round-trip.
  useEffect(() => {
    initAudioUnlock();
    const entries = animalAlphabetData.map((row) => ({
      letter: row.letter,
      animal: language === 'en' ? row.animalEn : row.animalEs,
      natureFact: language === 'en' ? row.funFactEn : row.funFactEs,
    }));
    void prefetchCombinedUtterances(entries, language, 6);
    return () => stopVoice();
  }, [language]);

  const speakLetter = (letter: string) => {
    const entry = byLetter[letter];
    if (!entry) return;
    playClickFeedback();
    speakLetterAnimalAndNature(letter, entry.animal, entry.natureFact, language);
  };

  const handleLetterTap = (letter: string) => {
    setSelectedLetter(letter);
    speakLetter(letter);
  };

  // Number narration: say the number then count up to it.
  // e.g. "Three! One, two, three." / "¡Tres! Uno, dos, tres."
  const NUMBER_WORDS_EN = [
    'zero', 'one', 'two', 'three', 'four', 'five',
    'six', 'seven', 'eight', 'nine', 'ten',
  ];
  const NUMBER_WORDS_ES = [
    'cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco',
    'seis', 'siete', 'ocho', 'nueve', 'diez',
  ];
  const numberUtterance = (n: number, lang: 'en' | 'es'): string => {
    const words = lang === 'en' ? NUMBER_WORDS_EN : NUMBER_WORDS_ES;
    const safe = Math.max(1, Math.min(words.length - 1, n));
    const sequence = words.slice(1, safe + 1).join(', ');
    const headline = words[safe];
    const cap = headline.charAt(0).toUpperCase() + headline.slice(1);
    return lang === 'en'
      ? `${cap}! Let's count: ${sequence}.`
      : `¡${cap}! Vamos a contar: ${sequence}.`;
  };

  const speakNumber = (n: number) => {
    playClickFeedback();
    void speak({ text: numberUtterance(n, language), language, playbackRate: 1.05 });
  };

  const handleNumberTap = (n: number) => {
    setSelectedNumber(n);
    speakNumber(n);
  };

  // Warm number cache so taps are immediate.
  useEffect(() => {
    numbers.forEach((n) => {
      void prefetch(numberUtterance(n, language), language);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const current = byLetter[selectedLetter];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#5EBD8B] to-[#48A589] rounded-2xl p-6 text-white">
        <h3 className="text-2xl font-bold mb-2">
          {t('Animal ABCs & 123s', 'ABCs de Animales y 123')}
        </h3>
        <p className="text-sm opacity-90">
          {t(
            'Tap a letter to hear the sound, the animal, and a nature note. Children can trace on paper while you narrate.',
            'Toca una letra para escuchar el sonido, el animal y una nota de naturaleza. Los niños pueden trazar en papel mientras narras.',
          )}
        </p>
      </div>

      {/* Letters Section — full A–Z, tap to speak */}
      <div>
        <h4 className="font-bold text-gray-800 mb-3">
          {t('Tap to hear · Trace on paper', 'Toca para escuchar · Traza en papel')}
        </h4>
        <div className="grid grid-cols-7 md:grid-cols-13 gap-2 mb-4">
          {animalAlphabetData.map((row) => (
            <button
              key={row.letter}
              onClick={() => handleLetterTap(row.letter)}
              className={`p-3 rounded-xl font-bold text-2xl transition-all ${
                selectedLetter === row.letter
                  ? 'bg-[#5EBD8B] text-white ring-2 ring-[#5EBD8B]'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              aria-label={`${row.letter} — ${language === 'en' ? row.animalEn : row.animalEs}`}
            >
              {row.letter}
            </button>
          ))}
        </div>

        {/* Large Letter Display */}
        <motion.div
          key={selectedLetter + language}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white border-4 border-[#5EBD8B] rounded-2xl p-8 text-center"
        >
          <div
            className="text-9xl font-bold text-[#5EBD8B] mb-4"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {selectedLetter}
          </div>
          {current && (
            <div>
              <div className="text-6xl mb-2">{current.emoji}</div>
              <p className="text-2xl font-bold text-gray-800 mb-3">
                {current.animal}
              </p>
              <p className="text-base text-gray-600 max-w-md mx-auto leading-relaxed">
                {current.natureFact}
              </p>
              <button
                type="button"
                onClick={() => speakLetter(selectedLetter)}
                className="mt-5 inline-flex items-center gap-2 bg-[#5EBD8B] hover:bg-[#48A589] text-white font-semibold px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all"
              >
                <Volume2 className="w-5 h-5" />
                {t('Hear it again', 'Escuchar otra vez')}
              </button>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-5 italic">
            {t(
              'Children trace the letter on paper while you read the nature note aloud.',
              'Los niños trazan la letra en papel mientras lees la nota de naturaleza en voz alta.',
            )}
          </p>
        </motion.div>
      </div>

      {/* Numbers Section */}
      <div>
        <h4 className="font-bold text-gray-800 mb-3">
          {t('Tap to count · Trace on paper', 'Toca para contar · Traza en papel')}
        </h4>
        <div className="grid grid-cols-5 md:grid-cols-10 gap-2 mb-4">
          {numbers.map((num) => (
            <button
              key={num}
              onClick={() => handleNumberTap(num)}
              className={`p-3 rounded-xl font-bold text-2xl transition-all ${
                selectedNumber === num
                  ? 'bg-[#5EBD8B] text-white ring-2 ring-[#5EBD8B]'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              aria-label={`${num}`}
            >
              {num}
            </button>
          ))}
        </div>

        {/* Large Number Display with Counting Objects */}
        <motion.div
          key={selectedNumber + language}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white border-4 border-[#5EBD8B] rounded-2xl p-8 text-center"
        >
          <div
            className="text-9xl font-bold text-[#5EBD8B] mb-4"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {selectedNumber}
          </div>
          <div className="flex justify-center gap-2 flex-wrap mb-4">
            {Array.from({ length: selectedNumber }).map((_, i) => (
              <span key={i} className="text-4xl">🦋</span>
            ))}
          </div>
          <button
            type="button"
            onClick={() => speakNumber(selectedNumber)}
            className="inline-flex items-center gap-2 bg-[#5EBD8B] hover:bg-[#48A589] text-white font-semibold px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all"
          >
            <Volume2 className="w-5 h-5" />
            {t('Count it again', 'Contar otra vez')}
          </button>
          <p className="text-sm text-gray-500 mt-4 italic">
            {t(
              'Children count along and trace the number on paper.',
              'Los niños cuentan y trazan el número en papel.',
            )}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

// Pepe's Breathing Exercise
export function PepeBreathingSimplified({ language }: SimplifiedGameProps) {
  const [isBreathing, setIsBreathing] = useState(false);
  const [breathCount, setBreathCount] = useState(0);

  const t = (en: string, es: string) => language === 'en' ? en : es;

  const startBreathing = () => {
    setIsBreathing(true);
    setBreathCount(0);
    let count = 0;
    const interval = setInterval(() => {
      count++;
      setBreathCount(count);
      if (count >= 6) {
        clearInterval(interval);
        setIsBreathing(false);
      }
    }, 8000); // 4s in, 4s out
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#48A589] to-[#358A73] rounded-2xl p-6 text-white flex items-center gap-4">
        <img
          src="/Pepe-intro-screen.svg"
          alt="Pepe"
          style={{ width: '60px', height: '60px', objectFit: 'contain' }}
        />
        <div>
          <h3 className="text-2xl font-bold mb-2">
            {t('Pepe\'s Breathing', 'Respiración de Pepe')}
          </h3>
          <p className="text-sm opacity-90">
            {t('Guided breathing exercise with Pepe the wise tree', 'Ejercicio de respiración guiado con Pepe el árbol sabio')}
          </p>
        </div>
      </div>

      <div
        className="rounded-2xl p-8 text-center"
        style={{
          background: 'linear-gradient(to bottom, #87CEEB 0%, #B0E0E6 40%, #90EE90 70%, #7CCD7C 100%)'
        }}
      >
        <div className="mb-6 flex justify-center">
          <motion.div
            animate={isBreathing ? {
              scale: [1, 1.15, 1],
              opacity: [0.9, 1, 0.9]
            } : {}}
            transition={{
              duration: 8,
              repeat: isBreathing ? Infinity : 0,
              ease: "easeInOut"
            }}
            className="inline-block"
          >
            <img
              src={
                !isBreathing
                  ? '/Pepe-respira-1-neutral.svg'
                  : breathCount % 2 === 1
                    ? '/Pepe-respira-2-inhale.svg'
                    : '/Pepe-respira-3-exhale.svg'
              }
              alt="Pepe the tree"
              style={{
                width: '364px',
                height: '364px',
                objectFit: 'contain'
              }}
            />
          </motion.div>
        </div>

        <h4 className="text-2xl font-bold text-gray-800 mb-4">
          {t('Breathe Like a Tree', 'Respira Como un Árbol')}
        </h4>

        {!isBreathing ? (
          <button
            onClick={startBreathing}
            className="bg-gradient-to-r from-[#48A589] to-[#358A73] text-white font-bold text-lg py-4 px-8 rounded-2xl hover:shadow-lg transition-all"
          >
            {t('Start Breathing', 'Comenzar Respiración')}
          </button>
        ) : (
          <div className="space-y-4">
            <motion.p
              key={breathCount}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xl font-semibold text-gray-700"
            >
              {breathCount % 2 === 1 ?
                t('Breathe in... like roots drawing water', 'Inhala... como raíces absorbiendo agua') :
                t('Breathe out... like leaves releasing into the wind', 'Exhala... como hojas soltando al viento')
              }
            </motion.p>
            <p className="text-gray-500">
              {t('Breath', 'Respiración')} {breathCount} / 6
            </p>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 rounded-xl">
          <p className="text-sm text-gray-600 italic">
            {t(
              'Guide children to breathe slowly: 4 seconds in through nose, 4 seconds out through mouth',
              'Guía a los niños a respirar lentamente: 4 segundos por la nariz, 4 segundos por la boca'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

// Peke's Story Sequence
export function PekeStorySimplified({ language }: SimplifiedGameProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const t = (en: string, es: string) => language === 'en' ? en : es;

  const storySlides = [
    {
      image: peke1Dream,
      question: { en: 'What is Peke dreaming about?', es: '¿Con qué está soñando Peke?' },
      prompt: { en: 'Tell me about Peke\'s dream...', es: 'Cuéntame sobre el sueño de Peke...' }
    },
    {
      image: peke2Thinking,
      question: { en: 'What thoughts and emotions might be flowing through Peke\'s body right now?', es: '¿Qué pensamientos y emociones podrían estar fluyendo por el cuerpo de Peke ahora mismo?' },
      prompt: { en: 'Share what\'s in Peke\'s heart...', es: 'Comparte lo que hay en el corazón de Peke...' }
    },
    {
      image: peke3Celebrating,
      question: { en: 'What might have brought this big, glowing smile and giggles to Peke\'s face?', es: '¿Qué podría haber traído esta gran sonrisa radiante y risitas a la cara de Peke?' },
      prompt: { en: 'Tell me the joyful ending...', es: 'Cuéntame el final alegre...' }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#10B981] to-[#8B5CF6] rounded-2xl p-6 text-white">
        <h3 className="text-2xl font-bold mb-2">
          {t('Peke\'s Adventures', 'Aventuras de Peke')}
        </h3>
        <p className="text-sm opacity-90">
          {t('Interactive storytelling with Peke the Boston Terrier', 'Narración interactiva con Peke el Boston Terrier')}
        </p>
      </div>

      <div className="bg-white rounded-2xl p-8">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-center mb-6"
        >
          <div className="mb-6 flex justify-center">
            <img
              src={storySlides[currentSlide].image}
              alt={storySlides[currentSlide].question[language]}
              className="w-96 h-64 object-cover rounded-2xl"
            />
          </div>
          <h4 className="text-2xl font-bold text-gray-800 mb-3">
            {storySlides[currentSlide].question[language]}
          </h4>
          <p className="text-lg text-gray-600 italic">
            {storySlides[currentSlide].prompt[language]}
          </p>
        </motion.div>

        {/* Slide Navigation */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
            disabled={currentSlide === 0}
            className="bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-bold px-6 py-2 rounded-xl transition-all"
          >
            ← {t('Previous', 'Anterior')}
          </button>
          <div className="flex gap-2">
            {storySlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentSlide === index ? 'bg-[#3BB257] w-8' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => setCurrentSlide(Math.min(storySlides.length - 1, currentSlide + 1))}
            disabled={currentSlide === storySlides.length - 1}
            className="bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-bold px-6 py-2 rounded-xl transition-all"
          >
            {t('Next', 'Siguiente')} →
          </button>
        </div>

        <div className="p-4 bg-blue-50 rounded-xl">
          <p className="text-sm text-gray-600 italic">
            {t(
              'Ask children to retell the story in their own words or draw the 3 scenes',
              'Pide a los niños que cuenten la historia con sus propias palabras o dibujen las 3 escenas'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

// Superpowers Cards - Two panels: Powers & Naming (mobile-optimized, no drawing)
export function SuperpowersCardsSimplified({ language }: SimplifiedGameProps) {
  const [selectedPowers, setSelectedPowers] = useState<string[]>([]);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);

  const t = (en: string, es: string) => language === 'en' ? en : es;

  // 8 Biotic Powers + 8 Abiotic Powers from original game
  const superpowers = [
    // Biotic (living things)
    { id: 'biotic-1', emoji: '🌸', name: { en: 'Flower Power', es: 'Poder de Flores' }, description: { en: 'Make flowers bloom wherever you go', es: 'Haz florecer flores donde vayas' }, color: '#EC4899' },
    { id: 'biotic-2', emoji: '🌳', name: { en: 'Tree Friend', es: 'Amigo de Árboles' }, description: { en: 'Talk to trees and help them grow', es: 'Habla con los árboles y ayúdalos a crecer' }, color: '#10B981' },
    { id: 'biotic-3', emoji: '🦋', name: { en: 'Butterfly Wings', es: 'Alas de Mariposa' }, description: { en: 'Fly with beautiful butterfly wings', es: 'Vuela con hermosas alas de mariposa' }, color: '#A855F7' },
    { id: 'biotic-4', emoji: '🐝', name: { en: 'Bee Buzz', es: 'Zumbido de Abeja' }, description: { en: 'Talk to bees and help flowers', es: 'Habla con las abejas y ayuda a las flores' }, color: '#F59E0B' },
    { id: 'biotic-5', emoji: '🐦', name: { en: 'Bird Song', es: 'Canto de Pájaro' }, description: { en: 'Sing beautiful songs with birds', es: 'Canta hermosas canciones con pájaros' }, color: '#06B6D4' },
    { id: 'biotic-6', emoji: '🍄', name: { en: 'Mushroom Magic', es: 'Magia de Hongos' }, description: { en: 'Grow magical mushrooms', es: 'Cultiva hongos mágicos' }, color: '#EF4444' },
    { id: 'biotic-7', emoji: '🌱', name: { en: 'Seed Singer', es: 'Cantante de Semillas' }, description: { en: 'Sing to seeds and watch them sprout', es: 'Canta a las semillas y míralas brotar' }, color: '#84CC16' },
    { id: 'biotic-8', emoji: '🦊', name: { en: 'Forest Friend', es: 'Amigo del Bosque' }, description: { en: 'Help all forest creatures be happy', es: 'Ayuda a todas las criaturas del bosque a ser felices' }, color: '#EA580C' },
    // Abiotic (non-living things)
    { id: 'abiotic-1', emoji: '☀️', name: { en: 'Sun Beam', es: 'Rayo de Sol' }, description: { en: 'Control sunlight and make rainbows', es: 'Controla la luz del sol y haz arcoíris' }, color: '#FBBF24' },
    { id: 'abiotic-2', emoji: '🌧️', name: { en: 'Rain Dancer', es: 'Bailarín de Lluvia' }, description: { en: 'Make it rain when plants are thirsty', es: 'Haz llover cuando las plantas tengan sed' }, color: '#3B82F6' },
    { id: 'abiotic-3', emoji: '🌬️', name: { en: 'Wind Whisperer', es: 'Susurrador del Viento' }, description: { en: 'Control the wind and spread seeds', es: 'Controla el viento y esparce semillas' }, color: '#60A5FA' },
    { id: 'abiotic-4', emoji: '🌊', name: { en: 'Wave Rider', es: 'Jinete de Olas' }, description: { en: 'Make waves and help sea creatures', es: 'Haz olas y ayuda a las criaturas marinas' }, color: '#0891B2' },
    { id: 'abiotic-5', emoji: '⚡', name: { en: 'Lightning Flash', es: 'Destello de Relámpago' }, description: { en: 'Make safe lightning shows in the sky', es: 'Haz espectáculos de relámpagos seguros en el cielo' }, color: '#A78BFA' },
    { id: 'abiotic-6', emoji: '🌈', name: { en: 'Rainbow Maker', es: 'Creador de Arcoíris' }, description: { en: 'Paint rainbows across the sky', es: 'Pinta arcoíris en el cielo' }, color: '#EC4899' },
    { id: 'abiotic-7', emoji: '❄️', name: { en: 'Snow Friend', es: 'Amigo de la Nieve' }, description: { en: 'Make beautiful snowflakes', es: 'Haz hermosos copos de nieve' }, color: '#0EA5E9' },
    { id: 'abiotic-8', emoji: '🌍', name: { en: 'Earth Shaper', es: 'Moldeador de Tierra' }, description: { en: 'Shape soil to help plants grow', es: 'Moldea la tierra para ayudar a crecer a las plantas' }, color: '#65A30D' },
  ];

  const nameWords = language === 'en'
    ? ['Sparkle', 'Shimmer', 'Twinkle', 'Rainbow', 'Sunny', 'Blossom', 'Sky', 'Star', 'Moon', 'River', 'Cloud', 'Leaf']
    : ['Brillo', 'Destello', 'Chispa', 'Arcoíris', 'Solecito', 'Flor', 'Cielo', 'Estrella', 'Luna', 'Río', 'Nube', 'Hoja'];

  const togglePower = (powerId: string) => {
    if (selectedPowers.includes(powerId)) {
      setSelectedPowers(selectedPowers.filter(p => p !== powerId));
    } else if (selectedPowers.length < 3) {
      setSelectedPowers([...selectedPowers, powerId]);
    }
  };

  const toggleWord = (word: string) => {
    if (selectedWords.includes(word)) {
      setSelectedWords(selectedWords.filter(w => w !== word));
    } else if (selectedWords.length < 3) {
      setSelectedWords([...selectedWords, word]);
    }
  };

  const creatureName = selectedWords.join('');

  return (
    <>
      {/* Panel 1: Choose Superpowers */}
      <div
        style={{
          alignSelf: 'stretch',
          marginTop: 16,
          padding: '20px 16px',
          borderRadius: 16,
          border: '2px solid rgba(53, 138, 115, 0.3)',
          background: 'linear-gradient(180deg, #E8F5F1 0%, #FFFEF9 100%)',
          maxHeight: 900,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontWeight: 800,
              fontSize: '16px',
              color: '#1E5A4D',
              marginBottom: 6,
            }}
          >
            ✨ {t('Creature Superpowers', 'Superpoderes de Criaturas')}
          </div>
          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '14px',
              color: '#4B5563',
              lineHeight: 1.45,
              marginBottom: 6,
            }}
          >
            {t('Pick 3 powers from nature - biotic (living) and abiotic (non-living)', 'Elige 3 poderes de la naturaleza - bióticos (vivos) y abióticos (no vivos)')}
          </div>
        </div>

        {selectedPowers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '8px 10px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, #D4EDE5 0%, #C1E5DB 100%)',
              border: '1px solid #358A73',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 700,
                fontSize: '13px',
                color: '#1E5A4D',
                marginBottom: 4,
              }}
            >
              {t('Selected Powers:', 'Poderes Seleccionados:')} ({selectedPowers.length}/3)
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {selectedPowers.map(powerId => {
                const power = superpowers.find(p => p.id === powerId);
                return power ? (
                  <span
                    key={powerId}
                    style={{
                      background: 'white',
                      padding: '4px 8px',
                      borderRadius: 12,
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>{power.emoji}</span>
                    {power.name[language]}
                  </span>
                ) : null;
              })}
            </div>
          </motion.div>
        )}

        <div
          style={{
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
            paddingRight: 4,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 20,
            }}
          >
            {superpowers.map(power => {
              const isSelected = selectedPowers.includes(power.id);
              return (
                <button
                  key={power.id}
                  type="button"
                  onClick={() => togglePower(power.id)}
                  title={`${power.name[language]} - ${power.description[language]}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 12,
                    padding: '28px 20px',
                    borderRadius: 20,
                    border: isSelected ? '4px solid #358A73' : '4px solid #C1E5DB',
                    background: isSelected ? '#E8F5F1' : '#fff',
                    cursor: 'pointer',
                    fontFamily: "'Nunito', sans-serif",
                    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                    boxShadow: isSelected ? '0 8px 20px rgba(53, 138, 115, 0.3)' : '0 4px 8px rgba(0,0,0,0.08)',
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'scale(0.97)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <span style={{ fontSize: '96px', lineHeight: 1 }}>{power.emoji}</span>
                  <span
                    style={{
                      fontSize: '28px',
                      fontWeight: 800,
                      color: '#374151',
                      textAlign: 'center',
                      lineHeight: 1.2,
                      maxWidth: '100%',
                    }}
                  >
                    {power.name[language]}
                  </span>
                  <span
                    style={{
                      fontSize: '20px',
                      fontWeight: 500,
                      color: '#6B7280',
                      textAlign: 'center',
                      lineHeight: 1.3,
                      maxWidth: '100%',
                    }}
                  >
                    {power.description[language]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Panel 2: Name Your Creature */}
      <div
        style={{
          alignSelf: 'stretch',
          marginTop: 16,
          padding: '12px 10px',
          borderRadius: 14,
          border: '1px solid rgba(139, 92, 246, 0.3)',
          background: 'linear-gradient(180deg, #F3E8FF 0%, #FFFEF9 100%)',
          maxHeight: 340,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontWeight: 800,
              fontSize: '16px',
              color: '#6B21A8',
              marginBottom: 6,
            }}
          >
            🎨 {t('Name Your Creature', 'Nombra Tu Criatura')}
          </div>
          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '14px',
              color: '#4B5563',
              lineHeight: 1.45,
              marginBottom: 6,
            }}
          >
            {t('Pick up to 3 words and combine them to create a unique name', 'Elige hasta 3 palabras y combínalas para crear un nombre único')}
          </div>
        </div>

        {/* Creature Name Preview */}
        {creatureName && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '8px 10px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 100%)',
              border: '1px solid #8B5CF6',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 700,
                fontSize: '13px',
                color: '#6B21A8',
                marginBottom: 2,
              }}
            >
              {t('Creature Name:', 'Nombre de Criatura:')}
            </div>
            <div
              style={{
                fontSize: '48px',
                fontWeight: 900,
                color: '#5B21B6',
                fontFamily: "'Nunito', sans-serif",
              }}
            >
              {creatureName}
            </div>
          </motion.div>
        )}

        <div
          style={{
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
            paddingRight: 4,
          }}
        >
          <p
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '20px',
              fontWeight: 600,
              color: '#6B7280',
              marginBottom: 12,
            }}
          >
            {t('Pick up to 3 words:', 'Elige hasta 3 palabras:')} ({selectedWords.length}/3)
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
            }}
          >
            {nameWords.map((word, i) => {
              const isSelected = selectedWords.includes(word);
              const selectionOrder = selectedWords.indexOf(word) + 1;
              const canSelect = selectedWords.length < 3 || isSelected;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleWord(word)}
                  disabled={!canSelect}
                  style={{
                    position: 'relative',
                    padding: '16px 12px',
                    borderRadius: 12,
                    fontSize: '24px',
                    fontWeight: 700,
                    fontFamily: "'Nunito', sans-serif",
                    background: isSelected
                      ? '#10B981'
                      : canSelect
                        ? '#D1FAE5'
                        : '#F3F4F6',
                    color: isSelected ? 'white' : canSelect ? '#047857' : '#9CA3AF',
                    border: isSelected ? '3px solid #FBBF24' : '3px solid transparent',
                    cursor: canSelect ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                  }}
                >
                  {isSelected && (
                    <span
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        width: 36,
                        height: 36,
                        background: '#FBBF24',
                        color: '#047857',
                        borderRadius: '50%',
                        fontSize: '20px',
                        fontWeight: 900,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {selectionOrder}
                    </span>
                  )}
                  {word}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
